import type { AnonymousTicketStatus, AnonymousTicketSummary, OperationalTimelineResponse } from "@queueryte/contracts";
import { useEffect, useMemo, useState, type KeyboardEvent } from "react";
import { formatDuration } from "./analytics";

interface DetailedOperationalTimelineProps {
  data:OperationalTimelineResponse|null;
  loading:boolean;
  selectedBucket:string|null;
  onSelectBucket:(bucket:string)=>void;
  onTicket:(ticket:AnonymousTicketSummary)=>void;
}

const FIFTEEN_MINUTES=15*60_000;

function median(values:number[]):number|null {
  if(values.length===0)return null;
  const ordered=[...values].sort((left,right)=>left-right);
  const middle=Math.floor(ordered.length/2);
  return ordered.length%2===0?Math.round(((ordered[middle-1]??0)+(ordered[middle]??0))/2):ordered[middle]??null;
}

function floorBucket(value:number):string {
  return new Date(Math.floor(value/FIFTEEN_MINUTES)*FIFTEEN_MINUTES).toISOString();
}

function ticketStatusLabel(value:AnonymousTicketStatus):string { return value==="serving"?"In service":value.charAt(0).toUpperCase()+value.slice(1); }

function timeLabel(value:number):string {
  return new Date(value).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit",hour12:false});
}

export function DetailedOperationalTimeline({
  data,loading,selectedBucket,onSelectBucket,onTicket,
}:DetailedOperationalTimelineProps) {
  const [zoom,setZoom]=useState(1);
  const [offset,setOffset]=useState(0);
  const [statuses,setStatuses]=useState<Set<AnonymousTicketStatus>>(new Set(["waiting","serving","completed"]));
  const [highlightOutliers,setHighlightOutliers]=useState(false);
  useEffect(()=>{setZoom(1);setOffset(0);},[data?.businessSessionId]);
  useEffect(()=>{
    if(selectedBucket===null||data?.businessStartedAt===null||data?.businessStartedAt===undefined)return;
    const fullStart=Date.parse(data.businessStartedAt);
    const fullEnd=Date.parse(data.businessEndedAt??new Date().toISOString());
    const selected=Date.parse(selectedBucket);
    if(!Number.isFinite(selected)||fullEnd<=fullStart)return;
    const span=(fullEnd-fullStart)/zoom;
    setOffset(Math.max(0,Math.min(1-1/zoom,(selected-fullStart-span/2)/(fullEnd-fullStart))));
  },[selectedBucket,data,zoom]);

  const domain=useMemo(()=>{
    const start=data?.businessStartedAt?Date.parse(data.businessStartedAt):0;
    const rawEnd=data?.businessEndedAt?Date.parse(data.businessEndedAt):Date.now();
    const end=Math.max(start+1,rawEnd);
    const full=end-start;
    const size=full/zoom;
    const visibleStart=start+full*offset;
    return {fullStart:start,fullEnd:end,start:visibleStart,end:Math.min(end,visibleStart+size)};
  },[data,zoom,offset]);
  const position=(value:string|null):number|null=>{
    if(value===null)return null;
    const timestamp=Date.parse(value);
    if(!Number.isFinite(timestamp)||timestamp<domain.start||timestamp>domain.end)return null;
    return (timestamp-domain.start)/(domain.end-domain.start)*100;
  };
  const visibleTickets=useMemo(()=>data?.tickets.filter((ticket)=>statuses.has(ticket.status))??[],[data,statuses]);
  const waitMedian=useMemo(()=>median((data?.tickets??[]).flatMap((ticket)=>ticket.waitSeconds===null?[]:[ticket.waitSeconds])),[data]);
  const selectedStart=selectedBucket===null?null:Date.parse(selectedBucket);
  const selectedPosition=selectedStart===null||!Number.isFinite(selectedStart)||selectedStart>domain.end||selectedStart+FIFTEEN_MINUTES<domain.start
    ?null:{
      left:Math.max(0,(selectedStart-domain.start)/(domain.end-domain.start)*100),
      width:Math.min(100,(FIFTEEN_MINUTES)/(domain.end-domain.start)*100),
    };
  const queuePoints=(data?.queueLength??[]).flatMap((sample)=>{
    const left=position(sample.occurredAt);
    return left===null?[]:[{left,value:sample.waitingCount}];
  });
  const queueMax=Math.max(1,...queuePoints.map((point)=>point.value));
  const queuePolyline=queuePoints.map((point)=>`${point.left},${88-point.value/queueMax*68}`).join(" ");
  const setZoomLevel=(value:number)=>{setZoom(Math.max(1,Math.min(6,value)));setOffset(0);};
  const pan=(direction:number)=>setOffset((current)=>Math.max(0,Math.min(1-1/zoom,current+direction*(.5/zoom))));
  const toggleStatus=(status:AnonymousTicketStatus)=>setStatuses((current)=>{
    const next=new Set(current);
    if(next.has(status))next.delete(status);else next.add(status);
    return next;
  });
  const keyboardPan=(event:KeyboardEvent<HTMLDivElement>)=>{
    if(event.key==="ArrowLeft"||event.key==="ArrowRight"){
      event.preventDefault();
      pan(event.key==="ArrowLeft"?-1:1);
    }
  };

  if(loading)return <section className="aggregate-timeline detailed-timeline"><div className="analytics-loading timeline-loading" role="status">Loading queue activity...</div></section>;
  if(data===null||data.businessStartedAt===null)return <section className="aggregate-timeline detailed-timeline">
    <header className="timeline-header"><div><p className="section-kicker">QUEUE DETAIL</p><h2>Business-day activity</h2><p>No reliable business-day opening time is available for this queue.</p></div></header>
  </section>;

  return <section className="aggregate-timeline detailed-timeline" id="analytics-timeline" tabIndex={-1} aria-labelledby="detailed-timeline-title">
    <header className="timeline-header">
      <div><p className="section-kicker">QUEUE DETAIL</p><h2 id="detailed-timeline-title">{data.queueName} activity</h2>
        <p>{data.siteName} | current business day | 15-minute intervals</p></div>
      <div className="timeline-controls" aria-label="Detailed timeline viewport controls">
        <button type="button" onClick={()=>{setZoom(1);setOffset(0);}}>Fit</button>
        <button type="button" onClick={()=>setZoomLevel(zoom-1)} disabled={zoom===1} aria-label="Zoom out">-</button>
        <output aria-live="polite">{zoom}x</output>
        <button type="button" onClick={()=>setZoomLevel(zoom+1)} disabled={zoom===6} aria-label="Zoom in">+</button>
        <button type="button" onClick={()=>pan(-1)} disabled={offset===0} aria-label="Pan earlier">&lt;</button>
        <button type="button" onClick={()=>pan(1)} disabled={offset>=1-1/zoom} aria-label="Pan later">&gt;</button>
      </div>
    </header>
    <div className="timeline-filter-row">
      <div className="timeline-status-filters" role="group" aria-label="Ticket status filters">
        {(["waiting","serving","completed"] as const).map((status)=><button key={status} type="button"
          className={statuses.has(status)?"active":""} aria-pressed={statuses.has(status)}
          onClick={()=>toggleStatus(status)}>{ticketStatusLabel(status)}</button>)}
      </div>
      <label className="outlier-toggle"><input type="checkbox" checked={highlightOutliers}
        onChange={(event)=>setHighlightOutliers(event.target.checked)}/>Highlight waits above median or target</label>
    </div>
    <div className="event-timeline-viewport" tabIndex={0} onKeyDown={keyboardPan}
      aria-label="Detailed business-day timeline. Use left and right arrow keys to pan.">
      <div className="event-timeline-labels" aria-hidden="true"><span>Issued</span><span>Service</span><span>Queue length</span><span>Time</span></div>
      <div className="event-timeline-canvas">
        {selectedPosition&&<button type="button" className="selected-time-window" style={{left:`${selectedPosition.left}%`,width:`${selectedPosition.width}%`}}
          onClick={()=>onSelectBucket(selectedBucket as string)} aria-label={`Selected interval ${timeLabel(selectedStart as number)}`}/>}
        {data.breaks.map((entry,index)=>{
          const left=position(entry.startedAt),right=position(entry.endedAt);
          return left===null||right===null?null:<span key={index} className="break-band" style={{left:`${left}%`,width:`${Math.max(.3,right-left)}%`}} title={`Break ${timeLabel(Date.parse(entry.startedAt))} to ${timeLabel(Date.parse(entry.endedAt))}`}/>;
        })}
        <span className="business-boundary start" title="Business day opened"/>{data.businessEndedAt&&position(data.businessEndedAt)!==null&&<span className="business-boundary end" title="Business day closed"/>}
        {data.isOpen&&position(new Date().toISOString())!==null&&<span className="event-now" style={{left:`${position(new Date().toISOString())}%`}}><i>Now</i></span>}
        <div className="event-lane issued-lane">{visibleTickets.map((ticket)=>{
          const left=position(ticket.issuedAt);if(left===null)return null;
          const outlier=highlightOutliers&&ticket.waitSeconds!==null&&((waitMedian!==null&&ticket.waitSeconds>waitMedian)||ticket.slaMet===false);
          return <button key={ticket.localTicketId} type="button" className={outlier?"ticket-marker issued outlier":"ticket-marker issued"}
            style={{left:`${left}%`}} onClick={()=>onTicket(ticket)}
            aria-label={`Ticket reference ${ticket.localTicketId.slice(0,8)} issued at ${timeLabel(Date.parse(ticket.issuedAt as string))}, ${ticketStatusLabel(ticket.status)}`}
            title={`Issued ${timeLabel(Date.parse(ticket.issuedAt as string))} | ${ticketStatusLabel(ticket.status)}`}/>;
        })}</div>
        <div className="event-lane service-lane">{visibleTickets.map((ticket)=>{
          const start=position(ticket.calledAt);if(start===null)return null;
          const completed=position(ticket.completedAt);const right=completed??Math.min(100,start+1);
          const outlier=highlightOutliers&&ticket.waitSeconds!==null&&((waitMedian!==null&&ticket.waitSeconds>waitMedian)||ticket.slaMet===false);
          return <button key={ticket.localTicketId} type="button" className={outlier?"service-duration outlier":"service-duration"}
            style={{left:`${start}%`,width:`${Math.max(.45,right-start)}%`}} onClick={()=>onTicket(ticket)}
            aria-label={`Ticket reference ${ticket.localTicketId.slice(0,8)}, ${ticketStatusLabel(ticket.status)}, wait time ${formatDuration(ticket.waitSeconds)}, service time ${formatDuration(ticket.serviceSeconds)}`}
            title={`Wait ${formatDuration(ticket.waitSeconds)} | Service ${formatDuration(ticket.serviceSeconds)}`}/>;
        })}</div>
        <div className="event-lane queue-lane">{queuePolyline&&<svg viewBox="0 0 100 100" preserveAspectRatio="none" role="img" aria-label="Sampled waiting count">
          <polyline points={queuePolyline} vectorEffect="non-scaling-stroke"/></svg>}<strong>{queueMax}</strong></div>
        <div className="event-time-axis"><span>{timeLabel(domain.start)}</span><button type="button" onClick={()=>onSelectBucket(floorBucket((domain.start+domain.end)/2))}>{timeLabel((domain.start+domain.end)/2)}</button><span>{data.isOpen?"":timeLabel(domain.end)}</span></div>
      </div>
    </div>
    <div className="timeline-selection" aria-live="polite"><strong>{data.tickets.length} tickets (anonymous)</strong>
      <span>Median wait time {formatDuration(waitMedian)}</span><span>Wait target {formatDuration(data.slaTargetSeconds)}</span>
      <span>{data.incompleteTimestampCount} without reliable clock time</span></div>
    {data.incompleteTimestampCount>0&&<details className="incomplete-tickets"><summary>{data.incompleteTimestampCount} tickets cannot be placed fully on the timeline</summary>
      <div>{data.tickets.filter((ticket)=>ticket.incompleteTimestamp).map((ticket)=><button type="button" key={ticket.localTicketId}
        onClick={()=>onTicket(ticket)}>{ticket.localTicketId.slice(0,8)} | {ticketStatusLabel(ticket.status)}</button>)}</div></details>}
  </section>;
}
